import { productImageAlt } from "@/lib/imageAlt";
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Check, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getShopifyImageUrl, ShopifyProduct, formatPrice } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { toast } from "sonner";
import { cleanProductTitle } from "@/lib/productTitle";
import { firstAvailableVariant, variantLabel } from "@/lib/variants";

type Variant = ShopifyProduct['node']['variants']['edges'][number]['node'];

interface CompleteTheLookProps {
  currentProduct: ShopifyProduct['node'];
  /** The variant the shopper has selected; the bundle adds exactly this one. */
  currentVariant?: Variant;
  recommendations: ShopifyProduct[];
}

export const CompleteTheLook = ({ currentProduct, currentVariant, recommendations }: CompleteTheLookProps) => {
  const addItem = useCartStore((state) => state.addItem);
  const isLoading = useCartStore((state) => state.isLoading);

  // Up to 3 complementary products, each with a variant that can be bought.
  const bundleProducts = useMemo(() => {
    return recommendations
      .filter((p) => p.node.id !== currentProduct.id)
      .map((p) => ({ product: p, variant: firstAvailableVariant(p.node) }))
      .filter((x): x is { product: ShopifyProduct; variant: Variant } => !!x.variant)
      .slice(0, 3);
  }, [recommendations, currentProduct.id]);

  // Nothing pre-selected — the shopper opts in to add-ons.
  const [selected, setSelected] = useState<Set<string>>(() => new Set<string>());


  const selectedProducts = bundleProducts.filter((x) => selected.has(x.product.node.id));

  const bundleTotal = useMemo(() => {
    let total = parseFloat(currentVariant?.price.amount || "0");
    selectedProducts.forEach((x) => {
      total += parseFloat(x.variant.price.amount || "0");
    });
    return total;
  }, [currentVariant, selectedProducts]);

  const currencyCode = currentProduct.priceRange?.minVariantPrice?.currencyCode || "USD";

  // Early return after all hooks. A sold-out selection has no bundle: its add
  // button would put an item in the cart that checkout then removes.
  if (bundleProducts.length === 0 || !currentVariant?.availableForSale) return null;

  const toggleProduct = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddAllToCart = async () => {
    const productsToAdd = [{ product: { node: currentProduct }, variant: currentVariant }, ...selectedProducts];

    // Count what Shopify really added: a sold-out piece is refused, and the cart store says so.
    let added = 0;
    for (const { product, variant } of productsToAdd) {
      const result = await addItem({
        product,
        variantId: variant.id,
        variantTitle: variant.title,
        price: variant.price,
        quantity: 1,
        selectedOptions: variant.selectedOptions || [],
      });
      if (result.addedQuantity > 0) added++;
    }

    if (added > 0) toast.success(`Added ${added} item${added === 1 ? "" : "s"} to cart`, { position: "top-center" });
  };

  return (
    <div className="rounded-2xl border border-border bg-sand/40 p-5 mt-6">
      <h3 className="text-[15px] font-bold text-foreground mb-4 flex items-center gap-2">
        <ShoppingBag className="h-4 w-4" />
        Complete the Look
      </h3>

      <div className="space-y-3">
        {/* Current product (always included) */}
        <div className="flex items-center gap-3 p-2 rounded-xl bg-background border border-border">
          <div className="w-14 h-14 bg-card overflow-hidden flex-shrink-0">
            {currentProduct.images?.edges?.[0]?.node && (
              <img
                src={getShopifyImageUrl(currentProduct.images.edges[0].node.url, 160)}
                alt={productImageAlt(currentProduct.title, { productType: currentProduct.productType })}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium truncate">{cleanProductTitle(currentProduct.title, currentProduct.productType)}</p>
            {/* The option selected, which the price is for: the name may state another size. */}
            <p className="text-[12px] text-muted-foreground truncate">
              This item{variantLabel(currentProduct, currentVariant) && ` · ${variantLabel(currentProduct, currentVariant)}`}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-[13px] font-semibold">
              {formatPrice(currentVariant.price.amount, currencyCode)}
            </span>
            <div className="w-5 h-5 rounded bg-primary flex items-center justify-center">
              <Check className="h-3 w-3 text-primary-foreground" />
            </div>
          </div>
        </div>

        {/* Recommended products */}
        {bundleProducts.map(({ product, variant }) => {
          const isSelected = selected.has(product.node.id);
          const name = cleanProductTitle(product.node.title, product.node.productType);
          // The row is a toggle button with the "View details" link beside it,
          // not inside it: a link nested in a button is invalid, and screen
          // readers announced the whole row as one unlabelled control.
          return (
            <div
              key={product.node.id}
              className={`relative flex items-center gap-3 p-2 border transition-colors ${
                isSelected ? "bg-background border-primary/40" : "bg-background/50 border-border hover:border-primary/30"
              }`}
            >
              {/* Stretched over the row, so a tap anywhere but the link still toggles.
                  One fixed label: aria-pressed says whether it is on. */}
              <button
                type="button"
                onClick={() => toggleProduct(product.node.id)}
                aria-pressed={isSelected}
                aria-label={`Add ${name} to the bundle`}
                className="absolute inset-0"
              />
              <div className="w-14 h-14 bg-card overflow-hidden flex-shrink-0">
                {product.node.images?.edges?.[0]?.node && (
                  <img
                    src={getShopifyImageUrl(product.node.images.edges[0].node.url, 160)}
                    alt={productImageAlt(product.node.title, { productType: product.node.productType })}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium truncate">{name}</p>
                <Link
                  to={`/product/${product.node.handle}`}
                  aria-label={`View details: ${name}`}
                  className="relative text-[11px] text-primary hover:underline"
                >
                  View details
                </Link>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[13px] font-semibold">
                  {formatPrice(variant.price.amount, currencyCode)}
                </span>
                <div className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${
                  isSelected ? "bg-primary" : "border border-border"
                }`}>
                  {isSelected ? (
                    <Check className="h-3 w-3 text-primary-foreground" />
                  ) : (
                    <Plus className="h-3 w-3 text-muted-foreground" />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bundle summary */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[13px] text-muted-foreground">
            {selectedProducts.length + 1} {selectedProducts.length === 0 ? "item" : "items"} selected
          </span>
          <span className="text-[16px] font-bold text-foreground">
            {formatPrice(bundleTotal.toString(), currencyCode)}
          </span>
        </div>
        <Button
          onClick={handleAddAllToCart}
          disabled={isLoading}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full text-[13px] tracking-[0.05em] uppercase font-bold h-11"
        >
          {isLoading ? "Adding..." : `Add ${selectedProducts.length + 1} ${selectedProducts.length === 0 ? "Item" : "Items"} to Cart`}
        </Button>
      </div>
    </div>
  );
};
